import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Tooltip } from '../ui/tooltip';
import { ChevronLeft, ChevronRight, CheckCircle, XCircle, AlertCircle, Calendar as CalendarIcon, Briefcase, X, Check, Pencil, Palmtree, Trash2, Filter, Kanban, ListChecks } from 'lucide-react';
import { GET_LEAVES_BY_DATE_RANGE, GET_ALL_EMPLOYEES, GET_HOLIDAYS_BY_DATE_RANGE, GET_WORK_DAYS_BY_DATE_RANGE, CREATE_LEAVE_MUTATION, UPDATE_LEAVE_MUTATION, DELETE_LEAVE_MUTATION, CREATE_WORK_DAY, UPDATE_WORK_DAY, DELETE_WORK_DAY, CREATE_HOLIDAY, UPDATE_HOLIDAY, DELETE_HOLIDAY, GET_LEAVE_TYPES, GET_PROJECTS_BY_DATE_RANGE, GET_TASKS_BY_DATE_RANGE } from '../../graphql/queries';
import { LeaveDialog, LeaveFormData } from './LeaveDialog';
import { LeaveDetailsDialog } from './LeaveDetailsDialog';
import { useAnyPermission, usePermission } from '../../hooks/usePermission';

interface Employee {
  id: string;
  firstName: string;
  lastName: string;
  department?: string;
  position?: string;
  hireDate?: string;
  terminationDate?: string | null;
  isActive?: boolean;
}

interface Leave {
  id: string;
  employeeId: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  status: string;
  reason?: string;
  approvedBy?: string;
  approvedAt?: string;
  employee: Employee;
}

interface CalendarProps {
  currentUserRole?: string;
}

interface CalendarProject {
  id: string;
  name: string;
  status: string;
  startDate?: string | null;
  plannedEndDate?: string | null;
  leaderId?: string;
  leader?: { id: string; firstName: string; lastName: string } | null;
}

interface CalendarTask {
  id: string;
  projectId: string;
  name: string;
  priority: string;
  startDate?: string | null;
  plannedEndDate?: string | null;
  project?: { id: string; name: string; status: string };
}

// Klucze warstw kalendarza zapisane w localStorage.
// Każda warstwa = osobny toggle w toolbarze.
const LAYER_KEYS = ['leaves', 'holidays', 'workDays', 'projects', 'tasks'] as const;
type LayerKey = (typeof LAYER_KEYS)[number];

const LAYER_LABELS: Record<LayerKey, string> = {
  leaves: 'Urlopy i zwolnienia',
  holidays: 'Święta',
  workDays: 'Dni robocze (przeniesienia)',
  projects: 'Projekty (terminy)',
  tasks: 'Zadania (terminy)',
};

const LAYER_STORAGE_KEY = 'calendar_visible_layers';

function loadVisibleLayers(): Record<LayerKey, boolean> {
  const defaults = LAYER_KEYS.reduce((acc, k) => ({ ...acc, [k]: true }), {} as Record<LayerKey, boolean>);
  try {
    const raw = localStorage.getItem(LAYER_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<Record<LayerKey, boolean>>;
    // Nowo dodane warstwy domyślnie włączone — nie zostaną wymazane przez stare zapisy.
    return { ...defaults, ...parsed } as Record<LayerKey, boolean>;
  } catch {
    return defaults;
  }
}

interface Holiday {
  id: string;
  name: string;
  date: string;
  isRecurring: boolean;
  description?: string;
}

interface HolidayFormData {
  name: string;
  date: string;
  isRecurring: boolean;
  description: string;
}

const FALLBACK_LEAVE_TYPES = [
  { value: 'VACATION', shortCode: 'UW', label: 'Urlop wypoczynkowy', color: 'bg-green-100 text-green-800' },
  { value: 'SICK_LEAVE', shortCode: 'ZL', label: 'Zwolnienie lekarskie', color: 'bg-red-100 text-red-800' },
  { value: 'PERSONAL', shortCode: 'UO', label: 'Urlop okolicznościowy', color: 'bg-blue-100 text-blue-800' },
  { value: 'MATERNITY', shortCode: 'UM', label: 'Urlop macierzyński', color: 'bg-pink-100 text-pink-800' },
  { value: 'PATERNITY', shortCode: 'UOJ', label: 'Urlop ojcowski', color: 'bg-purple-100 text-purple-800' },
  { value: 'UNPAID', shortCode: 'UB', label: 'Urlop bezpłatny', color: 'bg-gray-100 text-gray-800' },
  { value: 'OTHER', shortCode: 'IN', label: 'Inny', color: 'bg-yellow-100 text-yellow-800' },
];

const HIDDEN_LAYER_NOTE = 'ukryte — włącz w „Co pokazywać”';

const LEAVE_STATUS = [
  { value: 'PENDING', label: 'Oczekujący', color: 'bg-yellow-100 text-yellow-800', icon: AlertCircle },
  { value: 'APPROVED', label: 'Zatwierdzony', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { value: 'REJECTED', label: 'Odrzucony', color: 'bg-red-100 text-red-800', icon: XCircle },
  { value: 'CANCELLED', label: 'Anulowany', color: 'bg-gray-100 text-gray-800', icon: XCircle },
];

export const Calendar: React.FC<CalendarProps> = ({ currentUserRole }) => {
  const { data: leaveTypesData } = useQuery(GET_LEAVE_TYPES, {
    fetchPolicy: 'cache-first',
  });

  const leaveTypes = useMemo(() => {
    const fallbackColorMap: Record<string, string> = {};
    FALLBACK_LEAVE_TYPES.forEach(t => { fallbackColorMap[t.value] = t.color; });

    const dynamicTypes = (leaveTypesData?.leaveTypes || [])
      .filter((t: any) => t.isActive)
      .map((t: any) => ({
        value: t.code,
        label: t.displayName,
        color: fallbackColorMap[t.code] || 'bg-yellow-100 text-yellow-800',
      }));
    return dynamicTypes.length > 0 ? dynamicTypes : FALLBACK_LEAVE_TYPES;
  }, [leaveTypesData]);

  // Metadata for the per-day count badges. Includes inactive types too, so older
  // leaves of a since-disabled type still get a proper short code and name.
  const leaveTypeMeta = useMemo(() => {
    const fallbackByCode: Record<string, (typeof FALLBACK_LEAVE_TYPES)[number]> = {};
    FALLBACK_LEAVE_TYPES.forEach(t => { fallbackByCode[t.value] = t; });
    const meta: Record<string, { shortCode: string; label: string; color: string; order: number }> = {};
    FALLBACK_LEAVE_TYPES.forEach((t, i) => {
      meta[t.value] = { shortCode: t.shortCode, label: t.label, color: t.color, order: 1000 + i };
    });
    (leaveTypesData?.leaveTypes || []).forEach((t: any, i: number) => {
      meta[t.code] = {
        shortCode: t.shortCode || fallbackByCode[t.code]?.shortCode || String(t.code).slice(0, 3),
        label: t.displayName || t.label || t.code,
        color: fallbackByCode[t.code]?.color || 'bg-yellow-100 text-yellow-800',
        order: typeof t.sortOrder === 'number' ? t.sortOrder : i,
      };
    });
    return meta;
  }, [leaveTypesData]);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week'>('month');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState<Leave | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [showDayPopover, setShowDayPopover] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [showWorkDayDialog, setShowWorkDayDialog] = useState(false);
  const [workDayFormData, setWorkDayFormData] = useState({ name: '', description: '' });
  const [editingWorkDayId, setEditingWorkDayId] = useState<string | null>(null);
  const [prefilledStartDate, setPrefilledStartDate] = useState<string>('');
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [holidayFormData, setHolidayFormData] = useState<HolidayFormData>({ name: '', date: '', isRecurring: false, description: '' });
  const [editingHolidayId, setEditingHolidayId] = useState<string | null>(null);
  const [deleteConfirmHoliday, setDeleteConfirmHoliday] = useState<Holiday | null>(null);
  // `isAdmin` historically gated holiday/workday/leave management actions on the calendar.
  // Map to the matching dynamic permissions; treat any of them as enabling admin-mode controls.
  const isAdmin = useAnyPermission([
    'holidays.manage',
    'workdays.manage',
    'leaves.approve',
    'leaves.delete',
    'leaveTypes.manage',
  ]);

  // Calculate month boundaries
  const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  const calendarStart = new Date(monthStart);
  calendarStart.setDate(calendarStart.getDate() - monthStart.getDay());
  const calendarEnd = new Date(monthEnd);
  calendarEnd.setDate(calendarEnd.getDate() + (6 - monthEnd.getDay()));

  // Calculate week boundaries (Monday-Sunday, Polish convention)
  const getWeekMonday = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day; // Monday = 1, Sunday = 0 -> -6
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };
  const weekStart = getWeekMonday(currentDate);
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  // Use the wider range for data fetching to cover both modes
  const fetchStart = new Date(Math.min(calendarStart.getTime(), weekStart.getTime()));
  const fetchEnd = new Date(Math.max(calendarEnd.getTime(), weekEnd.getTime()));

  // Fetch employees for dropdown
  const { data: employeesData } = useQuery(GET_ALL_EMPLOYEES, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("✅ Employees loaded for calendar:", data.employees?.length);
    },
    onError: (error) => {
      console.error("❌ Error loading employees:", error);
    },
  });

  // Format local calendar date as YYYY-MM-DD (using local timezone)
  const formatLocalDate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const { data: leavesData, refetch: refetchLeaves } = useQuery(GET_LEAVES_BY_DATE_RANGE, {
    variables: {
      startDate: formatLocalDate(fetchStart) + 'T00:00:00.000Z',
      endDate: formatLocalDate(fetchEnd) + 'T00:00:00.000Z',
    },
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      console.log("✅ Leaves loaded:", data.leavesByDateRange?.length);
    },
    onError: (error) => {
      console.error("❌ Error loading leaves:", error);
    },
  });

  const { data: holidaysData, refetch: refetchHolidays } = useQuery(GET_HOLIDAYS_BY_DATE_RANGE, {
    variables: {
      startDate: formatLocalDate(fetchStart) + 'T00:00:00.000Z',
      endDate: formatLocalDate(fetchEnd) + 'T00:00:00.000Z',
    },
    fetchPolicy: "network-only",
  });

  const { data: workDaysData, refetch: refetchWorkDays } = useQuery(GET_WORK_DAYS_BY_DATE_RANGE, {
    variables: {
      startDate: formatLocalDate(fetchStart) + 'T00:00:00.000Z',
      endDate: formatLocalDate(fetchEnd) + 'T00:00:00.000Z',
    },
    fetchPolicy: "network-only",
  });

  // Projekty i zadania pobieramy w tym samym zakresie dat co pozostałe warstwy.
  // Backend filtruje już per-user (lider/członek/admin), więc nie potrzeba dodatkowej logiki.
  const { data: projectsData } = useQuery(GET_PROJECTS_BY_DATE_RANGE, {
    variables: {
      startDate: formatLocalDate(fetchStart),
      endDate: formatLocalDate(fetchEnd),
    },
    fetchPolicy: 'network-only',
  });

  const { data: tasksData } = useQuery(GET_TASKS_BY_DATE_RANGE, {
    variables: {
      startDate: formatLocalDate(fetchStart),
      endDate: formatLocalDate(fetchEnd),
    },
    fetchPolicy: 'network-only',
  });

  // Multi-select warstw z localStorage. Domyślnie wszystkie włączone.
  const [visibleLayers, setVisibleLayers] = useState<Record<LayerKey, boolean>>(() => loadVisibleLayers());
  useEffect(() => {
    try {
      localStorage.setItem(LAYER_STORAGE_KEY, JSON.stringify(visibleLayers));
    } catch {
      // localStorage może być wyłączony — ignoruj
    }
  }, [visibleLayers]);

  const [showLayerMenu, setShowLayerMenu] = useState(false);
  const layerMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!showLayerMenu) return;
    const onClick = (e: MouseEvent) => {
      if (layerMenuRef.current && !layerMenuRef.current.contains(e.target as Node)) {
        setShowLayerMenu(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [showLayerMenu]);

  const routerNavigate = useNavigate();

  const [createLeave] = useMutation(CREATE_LEAVE_MUTATION);
  const [updateLeave, { loading: updateLoading }] = useMutation(UPDATE_LEAVE_MUTATION);
  const [createWorkDay] = useMutation(CREATE_WORK_DAY);
  const [updateWorkDayMutation] = useMutation(UPDATE_WORK_DAY);
  const [deleteWorkDayMutation] = useMutation(DELETE_WORK_DAY);
  const [deleteLeave, { loading: deleteLoading }] = useMutation(DELETE_LEAVE_MUTATION);
  const [createHoliday] = useMutation(CREATE_HOLIDAY);
  const [updateHolidayMutation] = useMutation(UPDATE_HOLIDAY);
  const [deleteHolidayMutation] = useMutation(DELETE_HOLIDAY);

  const employees = employeesData?.employees || [];
  const leaves = leavesData?.leavesByDateRange || [];
  const holidays = holidaysData?.holidaysByDateRange || [];
  const workDays = workDaysData?.workDaysByDateRange || [];
  const projects: CalendarProject[] = projectsData?.projectsByDateRange || [];
  const tasks: CalendarTask[] = tasksData?.tasksByDateRange || [];

  const navigate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      if (direction === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
      }
    } else {
      // Week mode: navigate by 7 days
      newDate.setDate(newDate.getDate() + (direction === 'prev' ? -7 : 7));
    }
    setCurrentDate(newDate);
  };

  const navigateToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate week days (Monday to Sunday)
  const generateWeekDays = () => {
    const days = [];
    const current = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  // Format week header title (e.g., "24 - 30 marca 2026")
  // Use day+month format to get genitive case in Polish ("marca" not "marzec")
  const getMonthGenitive = (date: Date) => {
    // Format as "1 marca" then extract month name
    const formatted = date.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long' });
    return formatted.replace(/^\d+\s+/, '');
  };

  const getWeekTitle = () => {
    const startDay = weekStart.getDate();
    const endDay = weekEnd.getDate();
    const endYear = weekEnd.getFullYear();

    if (weekStart.getMonth() === weekEnd.getMonth()) {
      return `${startDay} - ${endDay} ${getMonthGenitive(weekEnd)} ${endYear}`;
    }
    // Week spans two months
    return `${startDay} ${getMonthGenitive(weekStart)} - ${endDay} ${getMonthGenitive(weekEnd)} ${endYear}`;
  };

  const generateCalendarDays = () => {
    const days = [];
    const current = new Date(calendarStart);

    while (current <= calendarEnd) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return days;
  };

  const getDayLeaves = (date: Date) => {
    const dateStr = formatLocalDate(date);
    return leaves.filter((leave: Leave) => {
      // API dates are midnight UTC from @db.Date, use toISOString to extract YYYY-MM-DD
      const startDate = new Date(leave.startDate).toISOString().split('T')[0];
      const endDate = new Date(leave.endDate).toISOString().split('T')[0];
      return dateStr >= startDate && dateStr <= endDate;
    });
  };

  // Helper: bezpieczna konwersja daty z API do lokalnego YYYY-MM-DD (Postgres @db.Date zwraca
  // half-northing UTC midnight, więc trzymamy się UTC-getter'ów żeby uniknąć przesunięcia o 1 dzień).
  const apiDateToLocal = (iso: string | null | undefined): string | null => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  // Projekty których ramka (start..plannedEnd) obejmuje ten dzień.
  const getDayProjects = (date: Date): CalendarProject[] => {
    const dateStr = formatLocalDate(date);
    return projects.filter((p) => {
      const start = apiDateToLocal(p.startDate);
      const end = apiDateToLocal(p.plannedEndDate);
      if (!start || !end) return false;
      return dateStr >= start && dateStr <= end;
    });
  };

  // Zadania których plannedEndDate przypada na ten dzień (= deadline na kalendarzu).
  const getDayTasks = (date: Date): CalendarTask[] => {
    const dateStr = formatLocalDate(date);
    return tasks.filter((t) => apiDateToLocal(t.plannedEndDate) === dateStr);
  };

  const isHoliday = (date: Date) => {
    // Use local date string to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return holidays.some((holiday: any) => {
      const holidayDate = new Date(holiday.date);
      const hYear = holidayDate.getUTCFullYear();
      const hMonth = String(holidayDate.getUTCMonth() + 1).padStart(2, '0');
      const hDay = String(holidayDate.getUTCDate()).padStart(2, '0');
      const holidayDateStr = `${hYear}-${hMonth}-${hDay}`;
      return dateStr === holidayDateStr;
    });
  };

  const isSunday = (date: Date) => {
    return date.getDay() === 0;
  };

  const getHolidayName = (date: Date) => {
    // Use local date string to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    const holiday = holidays.find((h: any) => {
      const holidayDate = new Date(h.date);
      const hYear = holidayDate.getUTCFullYear();
      const hMonth = String(holidayDate.getUTCMonth() + 1).padStart(2, '0');
      const hDay = String(holidayDate.getUTCDate()).padStart(2, '0');
      const holidayDateStr = `${hYear}-${hMonth}-${hDay}`;
      return dateStr === holidayDateStr;
    });
    return holiday?.name;
  };

  const isWorkDay = (date: Date) => {
    // Use local date string to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return workDays.some((workDay: any) => {
      const workDayDate = new Date(workDay.date);
      const wYear = workDayDate.getUTCFullYear();
      const wMonth = String(workDayDate.getUTCMonth() + 1).padStart(2, '0');
      const wDay = String(workDayDate.getUTCDate()).padStart(2, '0');
      const workDayDateStr = `${wYear}-${wMonth}-${wDay}`;
      return dateStr === workDayDateStr;
    });
  };

  const getWorkDayName = (date: Date) => {
    // Use local date string to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    const workDay = workDays.find((w: any) => {
      const workDayDate = new Date(w.date);
      const wYear = workDayDate.getUTCFullYear();
      const wMonth = String(workDayDate.getUTCMonth() + 1).padStart(2, '0');
      const wDay = String(workDayDate.getUTCDate()).padStart(2, '0');
      const workDayDateStr = `${wYear}-${wMonth}-${wDay}`;
      return dateStr === workDayDateStr;
    });
    return workDay?.name;
  };

  const getLeaveTypeConfig = (type: string) => {
    return leaveTypes.find((t: { value: string }) => t.value === type) || { value: type, label: type, color: 'bg-yellow-100 text-yellow-800' };
  };

  const getStatusConfig = (status: string) => {
    return LEAVE_STATUS.find(s => s.value === status) || LEAVE_STATUS[0];
  };

  // Per-day count badges. They count everything on the day regardless of the layer
  // toggles and tile limits, so nothing on the calendar can stay unnoticed.
  // Rejected/cancelled requests are not counted (nobody is absent because of them).
  // Clicks are not stopped, so a click on a badge bubbles up and opens the day popover.
  const renderDayBadges = (date: Date) => {
    type BadgeItem = {
      key: string;
      kind: 'leave' | 'leave-pending' | 'project' | 'task';
      text: string;
      count: number;
      tooltip: string;
      className: string;
      icon?: React.ReactNode;
      hidden: boolean;
    };
    const items: BadgeItem[] = [];
    const leavesHidden = !visibleLayers.leaves;

    const groups = new Map<string, { approved: number; pending: number }>();
    getDayLeaves(date).forEach((leave: Leave) => {
      if (leave.status !== 'APPROVED' && leave.status !== 'PENDING') return;
      const g = groups.get(leave.leaveType) || { approved: 0, pending: 0 };
      if (leave.status === 'APPROVED') g.approved += 1; else g.pending += 1;
      groups.set(leave.leaveType, g);
    });
    const metaFor = (code: string) =>
      leaveTypeMeta[code] || { shortCode: code.slice(0, 3), label: code, color: 'bg-yellow-100 text-yellow-800', order: 9999 };
    Array.from(groups.entries())
      .sort(([a], [b]) => metaFor(a).order - metaFor(b).order)
      .forEach(([code, g]) => {
        const meta = metaFor(code);
        if (g.approved > 0) {
          items.push({
            key: `leave-${code}`,
            kind: 'leave',
            text: meta.shortCode,
            count: g.approved,
            tooltip: `${meta.label}: ${g.approved}`,
            className: meta.color,
            hidden: leavesHidden,
          });
        }
        if (g.pending > 0) {
          items.push({
            key: `leave-pending-${code}`,
            kind: 'leave-pending',
            text: meta.shortCode,
            count: g.pending,
            tooltip: `${meta.label} — czeka na decyzję: ${g.pending}`,
            className: 'bg-amber-100 text-amber-800 border-dashed border-amber-500',
            icon: <AlertCircle className="w-2.5 h-2.5 shrink-0" />,
            hidden: leavesHidden,
          });
        }
      });

    const projectCount = getDayProjects(date).length;
    if (projectCount > 0) {
      items.push({
        key: 'projects',
        kind: 'project',
        text: '',
        count: projectCount,
        tooltip: `Projekty: ${projectCount}`,
        className: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/60 dark:text-indigo-200',
        icon: <Kanban className="w-2.5 h-2.5 shrink-0" />,
        hidden: !visibleLayers.projects,
      });
    }
    const taskCount = getDayTasks(date).length;
    if (taskCount > 0) {
      items.push({
        key: 'tasks',
        kind: 'task',
        text: '',
        count: taskCount,
        tooltip: `Zadania (terminy): ${taskCount}`,
        className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-200',
        icon: <ListChecks className="w-2.5 h-2.5 shrink-0" />,
        hidden: !visibleLayers.tasks,
      });
    }

    if (items.length === 0) return null;

    return (
      <div className="flex flex-wrap items-center gap-0.5 min-w-0 pt-0.5" data-testid={`day-badges-${formatLocalDate(date)}`}>
        {items.map((item) => (
          <Tooltip
            key={item.key}
            side="top"
            content={item.hidden ? `${item.tooltip} (${HIDDEN_LAYER_NOTE})` : item.tooltip}
          >
            <Badge
              variant="outline"
              data-kind={item.kind}
              data-hidden-layer={item.hidden ? 'true' : 'false'}
              aria-label={item.hidden ? `${item.tooltip} (${HIDDEN_LAYER_NOTE})` : item.tooltip}
              className={`gap-0.5 px-1 py-0 h-4 text-[10px] leading-none font-semibold cursor-pointer whitespace-nowrap focus:ring-0 focus:ring-offset-0 ${
                item.hidden
                  ? 'bg-transparent text-muted-foreground border-dashed border-muted-foreground/60 opacity-80'
                  : `${item.className} ${item.kind === 'leave-pending' ? '' : 'border-transparent'}`
              }`}
            >
              {item.icon}
              {item.text && <span>{item.text}</span>}
              <span>{item.count}</span>
            </Badge>
          </Tooltip>
        ))}
      </div>
    );
  };

  const handleAddLeave = async (formData: LeaveFormData) => {
    await createLeave({
      variables: {
        employeeId: formData.employeeId,
        leaveType: formData.leaveType,
        startDate: formData.startDate + 'T00:00:00.000Z',
        endDate: formData.endDate + 'T00:00:00.000Z',
        reason: formData.reason || null,
      },
    });
    refetchLeaves();
  };

  const handleApproveLeave = async (leaveId: string) => {
    await updateLeave({
      variables: {
        id: leaveId,
        status: 'APPROVED',
        approvedBy: 'admin',
      },
    });
    refetchLeaves();
    setShowDetailsDialog(false);
    setSelectedLeave(null);
  };

  const handleRejectLeave = async (leaveId: string) => {
    await updateLeave({
      variables: {
        id: leaveId,
        status: 'REJECTED',
        approvedBy: null,
      },
    });
    refetchLeaves();
    setShowDetailsDialog(false);
    setSelectedLeave(null);
  };

  const handleDeleteLeave = async (leaveId: string) => {
    await deleteLeave({
      variables: { id: leaveId },
    });
    refetchLeaves();
    setShowDetailsDialog(false);
    setSelectedLeave(null);
  };

  // Get WorkDay object for a date (to get its ID for deletion)
  const getWorkDayForDate = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return workDays.find((w: any) => {
      const workDayDate = new Date(w.date);
      const wYear = workDayDate.getUTCFullYear();
      const wMonth = String(workDayDate.getUTCMonth() + 1).padStart(2, '0');
      const wDay = String(workDayDate.getUTCDate()).padStart(2, '0');
      const workDayDateStr = `${wYear}-${wMonth}-${wDay}`;
      return dateStr === workDayDateStr;
    });
  };

  // Get Holiday object for a date
  const getHolidayForDate = (date: Date): Holiday | undefined => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    return holidays.find((h: any) => {
      const holidayDate = new Date(h.date);
      const hYear = holidayDate.getUTCFullYear();
      const hMonth = String(holidayDate.getUTCMonth() + 1).padStart(2, '0');
      const hDay = String(holidayDate.getUTCDate()).padStart(2, '0');
      const holidayDateStr = `${hYear}-${hMonth}-${hDay}`;
      return dateStr === holidayDateStr;
    });
  };

  // Handle "Add holiday" from popover
  const handleAddHolidayFromPopover = () => {
    if (!selectedDate) return;
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    setHolidayFormData({ name: '', date: `${year}-${month}-${day}`, isRecurring: false, description: '' });
    setEditingHolidayId(null);
    setShowDayPopover(false);
    setShowHolidayDialog(true);
  };

  // Handle "Edit holiday" from popover
  const handleEditHolidayFromPopover = () => {
    if (!selectedDate) return;
    const holiday = getHolidayForDate(selectedDate);
    if (holiday) {
      setHolidayFormData({
        name: holiday.name,
        date: new Date(holiday.date).toISOString().split('T')[0],
        isRecurring: holiday.isRecurring,
        description: holiday.description || '',
      });
      setEditingHolidayId(holiday.id);
      setShowDayPopover(false);
      setShowHolidayDialog(true);
    }
  };

  // Handle "Delete holiday" from popover
  const handleDeleteHolidayFromPopover = () => {
    if (!selectedDate) return;
    const holiday = getHolidayForDate(selectedDate);
    if (holiday) {
      setDeleteConfirmHoliday(holiday);
      setShowDayPopover(false);
    }
  };

  // Save holiday (create or update)
  const handleSaveHoliday = async () => {
    if (!holidayFormData.name || !holidayFormData.date) return;

    try {
      if (editingHolidayId) {
        await updateHolidayMutation({
          variables: {
            id: editingHolidayId,
            name: holidayFormData.name,
            date: holidayFormData.date + 'T00:00:00.000Z',
            isRecurring: holidayFormData.isRecurring,
            description: holidayFormData.description || null,
          },
        });
      } else {
        await createHoliday({
          variables: {
            name: holidayFormData.name,
            date: holidayFormData.date + 'T00:00:00.000Z',
            isRecurring: holidayFormData.isRecurring,
            description: holidayFormData.description || null,
          },
        });
      }
      await refetchHolidays();
      setShowHolidayDialog(false);
      setEditingHolidayId(null);
      setHolidayFormData({ name: '', date: '', isRecurring: false, description: '' });
      toast.success(editingHolidayId ? 'Święto zaktualizowane' : 'Święto dodane');
    } catch (err: any) {
      toast.error(err.message || 'Błąd podczas zapisywania święta');
    }
  };

  // Confirm delete holiday
  const handleConfirmDeleteHoliday = async () => {
    if (!deleteConfirmHoliday) return;
    try {
      await deleteHolidayMutation({ variables: { id: deleteConfirmHoliday.id } });
      await refetchHolidays();
      setDeleteConfirmHoliday(null);
      toast.success('Święto usunięte');
    } catch (err: any) {
      toast.error(err.message || 'Błąd podczas usuwania święta');
    }
  };

  // Handle day click - show popover
  const handleDayClick = (e: React.MouseEvent, date: Date) => {
    e.stopPropagation();
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setPopoverPosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 5,
    });
    setSelectedDate(date);
    setShowDayPopover(true);
  };

  // Close popover when clicking outside
  const closeDayPopover = () => {
    setShowDayPopover(false);
    setSelectedDate(null);
  };

  // Handle "Add leave" from popover
  const handleAddLeaveFromPopover = () => {
    if (selectedDate) {
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      setPrefilledStartDate(`${year}-${month}-${day}`);
    }
    setShowDayPopover(false);
    setShowAddDialog(true);
  };

  // Handle "Add work day" from popover
  const handleAddWorkDayFromPopover = () => {
    setShowDayPopover(false);
    setWorkDayFormData({ name: '', description: '' });
    setEditingWorkDayId(null);
    setShowWorkDayDialog(true);
  };

  // Handle "Edit work day" from popover
  const handleEditWorkDayFromPopover = () => {
    if (!selectedDate) return;
    const workDay = getWorkDayForDate(selectedDate);
    if (workDay) {
      setWorkDayFormData({
        name: workDay.name,
        description: workDay.description || '',
      });
      setEditingWorkDayId(workDay.id);
      setShowDayPopover(false);
      setShowWorkDayDialog(true);
    }
  };

  // Handle "Remove work day" from popover
  const handleRemoveWorkDay = async () => {
    if (!selectedDate) return;
    const workDay = getWorkDayForDate(selectedDate);
    if (workDay) {
      await deleteWorkDayMutation({ variables: { id: workDay.id } });
      refetchWorkDays();
    }
    setShowDayPopover(false);
    setSelectedDate(null);
  };

  // Save work day (create or update)
  const handleSaveWorkDay = async () => {
    if (!selectedDate || !workDayFormData.name) return;

    if (editingWorkDayId) {
      // Update existing work day
      await updateWorkDayMutation({
        variables: {
          id: editingWorkDayId,
          name: workDayFormData.name,
          description: workDayFormData.description || null,
        },
      });
    } else {
      // Create new work day
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}T00:00:00.000Z`;

      await createWorkDay({
        variables: {
          name: workDayFormData.name,
          date: dateStr,
          description: workDayFormData.description || null,
        },
      });
    }
    refetchWorkDays();
    setShowWorkDayDialog(false);
    setSelectedDate(null);
    setEditingWorkDayId(null);
    setWorkDayFormData({ name: '', description: '' });
  };

  const calendarDays = generateCalendarDays();
  const isCurrentMonth = (date: Date) => date.getMonth() === currentDate.getMonth();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Kalendarz</h2>
          <p className="text-muted-foreground">Kliknij na dzień, aby dodać nieobecność, święto lub oznaczyć dzień roboczy</p>
        </div>
      </div>

      {/* Calendar Navigation */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('prev')}
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={navigateToToday}
              >
                Dziś
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('next')}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <CardTitle className="text-xl">
              {viewMode === 'month'
                ? currentDate.toLocaleDateString('pl-PL', { month: 'long', year: 'numeric' })
                : getWeekTitle()
              }
            </CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative" ref={layerMenuRef}>
                {(() => {
                  const visibleCount = LAYER_KEYS.filter((k) => visibleLayers[k]).length;
                  const someHidden = visibleCount < LAYER_KEYS.length;
                  return (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowLayerMenu((v) => !v)}
                      data-testid="calendar-layers-button"
                      data-some-hidden={someHidden ? 'true' : 'false'}
                      title={someHidden ? 'Część informacji jest ukryta w kalendarzu' : undefined}
                      className={`text-xs h-7 ${
                        someHidden
                          ? 'border-amber-500 bg-amber-100 text-amber-900 hover:bg-amber-200 hover:text-amber-950 dark:border-amber-500 dark:bg-amber-900/40 dark:text-amber-200 dark:hover:bg-amber-900/60 dark:hover:text-amber-100'
                          : ''
                      }`}
                    >
                      <Filter className="w-3.5 h-3.5 mr-1" />
                      Co pokazywać ({visibleCount}/{LAYER_KEYS.length})
                      {someHidden && <span className="ml-1 font-semibold hidden xl:inline">· część ukryta</span>}
                    </Button>
                  );
                })()}
                {showLayerMenu && (
                  <div className="absolute right-0 top-full mt-1 z-50 bg-white dark:bg-gray-900 border rounded-md shadow-lg p-2 w-64">
                    <div className="text-xs font-medium text-muted-foreground px-2 py-1">Warstwy kalendarza</div>
                    {LAYER_KEYS.map((k) => (
                      <label key={k} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-800 rounded cursor-pointer text-sm">
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={visibleLayers[k]}
                          onChange={(e) =>
                            setVisibleLayers((prev) => ({ ...prev, [k]: e.target.checked }))
                          }
                        />
                        <span>{LAYER_LABELS[k]}</span>
                      </label>
                    ))}
                    <div className="border-t mt-1 pt-1 flex gap-2 px-2">
                      <button
                        type="button"
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => setVisibleLayers(LAYER_KEYS.reduce((a, k) => ({ ...a, [k]: true }), {} as Record<LayerKey, boolean>))}
                      >
                        Zaznacz wszystkie
                      </button>
                      <button
                        type="button"
                        className="text-xs text-gray-600 hover:underline"
                        onClick={() => setVisibleLayers(LAYER_KEYS.reduce((a, k) => ({ ...a, [k]: false }), {} as Record<LayerKey, boolean>))}
                      >
                        Odznacz wszystkie
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1 border rounded-md p-0.5">
                <Button
                  variant={viewMode === 'month' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('month')}
                  className="text-xs h-7 px-3"
                >
                  Miesiąc
                </Button>
                <Button
                  variant={viewMode === 'week' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('week')}
                  className="text-xs h-7 px-3"
                >
                  Tydzień
                </Button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {viewMode === 'month' ? (
            /* Month Calendar Grid */
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers */}
              {['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So'].map(day => (
                <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                  {day}
                </div>
              ))}

              {/* Calendar days */}
              {calendarDays.map(date => {
                const dayLeaves = visibleLayers.leaves ? getDayLeaves(date) : [];
                const dayProjects = visibleLayers.projects ? getDayProjects(date) : [];
                const dayTasks = visibleLayers.tasks ? getDayTasks(date) : [];
                const isToday = date.toDateString() === new Date().toDateString();
                const isCurrentMonthDay = isCurrentMonth(date);
                const isDayHoliday = visibleLayers.holidays && isHoliday(date);
                const isDaySunday = isSunday(date);
                const isDayWorkDay = visibleLayers.workDays && isWorkDay(date);
                const holidayName = getHolidayName(date);
                const workDayName = getWorkDayName(date);

                // Determine day styling
                // Work days (forced work days) have priority - they override weekend/holiday styling
                const getDayClasses = () => {
                  if (isDayWorkDay) {
                    // Forced work day - amber/yellow styling
                    return 'bg-amber-50 border-amber-300 dark:bg-amber-950/30 dark:border-amber-700/50';
                  }
                  if (isDayHoliday || isDaySunday) {
                    // Holiday or Sunday - red styling
                    return 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800/50';
                  }
                  if (isCurrentMonthDay) {
                    return 'bg-card border-border';
                  }
                  return 'bg-muted/50 border-border';
                };

                const getTextClasses = () => {
                  if (isDayWorkDay) {
                    return 'text-amber-700 dark:text-amber-400';
                  }
                  if (isDayHoliday || isDaySunday) {
                    return 'text-red-600 dark:text-red-400';
                  }
                  if (isCurrentMonthDay) {
                    return 'text-foreground';
                  }
                  return 'text-muted-foreground';
                };

                return (
                  <div
                    key={date.toISOString()}
                    onClick={(e) => handleDayClick(e, date)}
                    data-date={formatLocalDate(date)}
                    className={`min-h-[100px] min-w-0 p-1 border transition-colors cursor-pointer hover:ring-2 hover:ring-primary/50 ${getDayClasses()} ${isToday ? 'border-primary' : ''}`}
                  >
                    <div className="flex items-start gap-1">
                      <div className={`text-sm font-medium shrink-0 ${getTextClasses()} ${isToday ? 'text-primary' : ''}`}>
                        {date.getDate()}
                      </div>
                      {renderDayBadges(date)}
                    </div>

                    {isDayWorkDay && (
                      <div className="text-xs text-amber-700 dark:text-amber-400 font-medium mb-1">
                        {workDayName || 'Dzien roboczy'}
                      </div>
                    )}

                    {!isDayWorkDay && (isDayHoliday || isDaySunday) && (
                      <div className="text-xs text-red-600 dark:text-red-400 font-medium mb-1">
                        {holidayName || 'Niedziela'}
                      </div>
                    )}

                    <div className="mt-1 space-y-1">
                      {dayLeaves.slice(0, 3).map((leave: Leave) => {
                        const typeConfig = getLeaveTypeConfig(leave.leaveType);
                        const statusConfig = getStatusConfig(leave.status);
                        const StatusIcon = statusConfig.icon;

                        const tileColor = leave.status === 'APPROVED'
                          ? typeConfig.color
                          : leave.status === 'PENDING'
                          ? 'bg-amber-100 text-amber-800 border-l-2 border-amber-500'
                          : leave.status === 'REJECTED'
                          ? 'bg-red-50 text-red-400 line-through opacity-60'
                          : 'bg-gray-50 text-gray-400 line-through opacity-60';

                        return (
                          <div
                            key={leave.id}
                            className={`text-xs p-1 rounded cursor-pointer ${tileColor}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLeave(leave);
                              setShowDetailsDialog(true);
                            }}
                          >
                            <div className="flex items-center space-x-1">
                              <StatusIcon className="w-3 h-3" />
                              <span className="truncate">
                                {leave.employee.lastName} {leave.employee.firstName}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                      {dayLeaves.length > 3 && (
                        <div className="text-xs text-primary font-medium cursor-pointer hover:underline">
                          +{dayLeaves.length - 3} więcej
                        </div>
                      )}

                      {dayProjects.slice(0, 2).map((p) => (
                        <div
                          key={`proj-${p.id}`}
                          className="text-xs p-1 rounded cursor-pointer bg-indigo-100 text-indigo-800 border-l-2 border-indigo-500 dark:bg-indigo-950/40 dark:text-indigo-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            routerNavigate(`/projects/${p.id}`);
                          }}
                          title={`Projekt: ${p.name}`}
                        >
                          <div className="flex items-center gap-1">
                            <Kanban className="w-3 h-3 shrink-0" />
                            <span className="truncate">{p.name}</span>
                          </div>
                        </div>
                      ))}
                      {dayProjects.length > 2 && (
                        <div className="text-xs text-indigo-700 dark:text-indigo-300 font-medium">
                          +{dayProjects.length - 2} proj.
                        </div>
                      )}

                      {dayTasks.slice(0, 2).map((t) => (
                        <div
                          key={`task-${t.id}`}
                          className="text-xs p-1 rounded cursor-pointer bg-emerald-100 text-emerald-800 border-l-2 border-emerald-500 dark:bg-emerald-950/40 dark:text-emerald-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (t.projectId) routerNavigate(`/projects/${t.projectId}`);
                          }}
                          title={`Termin zadania: ${t.name}${t.project ? ' (' + t.project.name + ')' : ''}`}
                        >
                          <div className="flex items-center gap-1">
                            <ListChecks className="w-3 h-3 shrink-0" />
                            <span className="truncate">{t.name}</span>
                          </div>
                        </div>
                      ))}
                      {dayTasks.length > 2 && (
                        <div className="text-xs text-emerald-700 dark:text-emerald-300 font-medium">
                          +{dayTasks.length - 2} zad.
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Week View Grid */
            <div className="grid grid-cols-7 gap-1">
              {/* Day headers with full names and dates */}
              {generateWeekDays().map(date => {
                const isToday = date.toDateString() === new Date().toDateString();
                const isDayHoliday = isHoliday(date);
                const isDaySunday = isSunday(date);
                const isDayWorkDay = isWorkDay(date);

                const getHeaderTextColor = () => {
                  if (isDayWorkDay) return 'text-amber-700 dark:text-amber-400';
                  if (isDayHoliday || isDaySunday) return 'text-red-600 dark:text-red-400';
                  return 'text-muted-foreground';
                };

                return (
                  <div
                    key={date.toISOString() + '-header'}
                    className={`p-2 text-center ${getHeaderTextColor()}`}
                  >
                    <div className="text-sm font-medium">
                      {['Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'So', 'Nd'][(date.getDay() + 6) % 7]}
                    </div>
                    <div className={`text-lg font-bold ${isToday ? 'text-primary' : ''}`}>
                      {date.getDate()}
                    </div>
                  </div>
                );
              })}

              {/* Week day cells */}
              {generateWeekDays().map(date => {
                const dayLeaves = visibleLayers.leaves ? getDayLeaves(date) : [];
                const dayProjects = visibleLayers.projects ? getDayProjects(date) : [];
                const dayTasks = visibleLayers.tasks ? getDayTasks(date) : [];
                const isToday = date.toDateString() === new Date().toDateString();
                const isDayHoliday = visibleLayers.holidays && isHoliday(date);
                const isDaySunday = isSunday(date);
                const isDayWorkDay = visibleLayers.workDays && isWorkDay(date);
                const holidayName = getHolidayName(date);
                const workDayName = getWorkDayName(date);

                const getDayClasses = () => {
                  if (isDayWorkDay) {
                    return 'bg-amber-50 border-amber-300 dark:bg-amber-950/30 dark:border-amber-700/50';
                  }
                  if (isDayHoliday || isDaySunday) {
                    return 'bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800/50';
                  }
                  return 'bg-card border-border';
                };

                return (
                  <div
                    key={date.toISOString()}
                    onClick={(e) => handleDayClick(e, date)}
                    data-date={formatLocalDate(date)}
                    className={`min-h-[200px] min-w-0 p-2 border transition-colors cursor-pointer hover:ring-2 hover:ring-primary/50 ${getDayClasses()} ${isToday ? 'border-primary' : ''}`}
                  >
                    {/* The day number lives in the header row above; badges sit at the top of the cell. */}
                    {(() => {
                      const badges = renderDayBadges(date);
                      return badges ? <div className="mb-1.5">{badges}</div> : null;
                    })()}

                    {isDayWorkDay && (
                      <div className="text-xs text-amber-700 dark:text-amber-400 font-medium mb-2">
                        {workDayName || 'Dzien roboczy'}
                      </div>
                    )}

                    {!isDayWorkDay && (isDayHoliday || isDaySunday) && (
                      <div className="text-xs text-red-600 dark:text-red-400 font-medium mb-2">
                        {holidayName || 'Niedziela'}
                      </div>
                    )}

                    <div className="space-y-1">
                      {/* Show ALL leaves in week view - no limit */}
                      {dayLeaves.map((leave: Leave) => {
                        const typeConfig = getLeaveTypeConfig(leave.leaveType);
                        const statusConfig = getStatusConfig(leave.status);
                        const StatusIcon = statusConfig.icon;

                        const tileColor = leave.status === 'APPROVED'
                          ? typeConfig.color
                          : leave.status === 'PENDING'
                          ? 'bg-amber-100 text-amber-800 border-l-2 border-amber-500'
                          : leave.status === 'REJECTED'
                          ? 'bg-red-50 text-red-400 line-through opacity-60'
                          : 'bg-gray-50 text-gray-400 line-through opacity-60';

                        return (
                          <div
                            key={leave.id}
                            className={`text-xs p-1.5 rounded cursor-pointer hover:opacity-80 transition-opacity ${tileColor}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedLeave(leave);
                              setShowDetailsDialog(true);
                            }}
                          >
                            <div className="flex items-center space-x-1">
                              <StatusIcon className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate font-medium">
                                {leave.employee.lastName} {leave.employee.firstName}
                              </span>
                            </div>
                            <div className="text-[10px] opacity-75 ml-4 truncate">
                              {typeConfig.label}
                            </div>
                          </div>
                        );
                      })}

                      {dayProjects.map((p) => (
                        <div
                          key={`week-proj-${p.id}`}
                          className="text-xs p-1.5 rounded cursor-pointer hover:opacity-80 bg-indigo-100 text-indigo-800 border-l-2 border-indigo-500 dark:bg-indigo-950/40 dark:text-indigo-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            routerNavigate(`/projects/${p.id}`);
                          }}
                          title={`Projekt: ${p.name}`}
                        >
                          <div className="flex items-center space-x-1">
                            <Kanban className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate font-medium">{p.name}</span>
                          </div>
                          <div className="text-[10px] opacity-75 ml-4 truncate">Projekt</div>
                        </div>
                      ))}

                      {dayTasks.map((t) => (
                        <div
                          key={`week-task-${t.id}`}
                          className="text-xs p-1.5 rounded cursor-pointer hover:opacity-80 bg-emerald-100 text-emerald-800 border-l-2 border-emerald-500 dark:bg-emerald-950/40 dark:text-emerald-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (t.projectId) routerNavigate(`/projects/${t.projectId}`);
                          }}
                          title={`Termin zadania: ${t.name}${t.project ? ' (' + t.project.name + ')' : ''}`}
                        >
                          <div className="flex items-center space-x-1">
                            <ListChecks className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate font-medium">{t.name}</span>
                          </div>
                          {t.project && (
                            <div className="text-[10px] opacity-75 ml-4 truncate">{t.project.name}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Day Popover */}
      {showDayPopover && selectedDate && (
        <>
          {/* Backdrop to close popover */}
          <div
            className="fixed inset-0 z-40"
            onClick={closeDayPopover}
          />
          {/* Popover */}
          <div
            className="fixed z-50 bg-card border rounded-lg shadow-lg p-2 w-[250px]"
            style={{
              left: `${popoverPosition.x}px`,
              top: `${popoverPosition.y}px`,
              transform: 'translateX(-50%)',
            }}
          >
            <div className="text-sm font-medium text-muted-foreground mb-2 px-2">
              {selectedDate.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' })}
            </div>
            {/* Leaves list for selected day */}
            {(() => {
              const popoverLeaves = getDayLeaves(selectedDate);
              if (popoverLeaves.length === 0) return null;
              return (
                <div className="mb-2">
                  <div className="space-y-1 max-h-[200px] overflow-y-auto px-1">
                    {popoverLeaves.map((leave: Leave) => {
                      const typeConfig = getLeaveTypeConfig(leave.leaveType);
                      const statusConfig = getStatusConfig(leave.status);
                      const StatusIcon = statusConfig.icon;

                      const tileColor = leave.status === 'APPROVED'
                        ? typeConfig.color
                        : leave.status === 'PENDING'
                        ? 'bg-amber-100 text-amber-800 border-l-2 border-amber-500'
                        : leave.status === 'REJECTED'
                        ? 'bg-red-50 text-red-400 line-through opacity-60'
                        : 'bg-gray-50 text-gray-400 line-through opacity-60';

                      return (
                        <button
                          key={leave.id}
                          className={`w-full text-left text-xs p-1.5 rounded cursor-pointer hover:opacity-80 transition-opacity ${tileColor}`}
                          onClick={() => {
                            setShowDayPopover(false);
                            setSelectedLeave(leave);
                            setShowDetailsDialog(true);
                          }}
                        >
                          <div className="flex items-center space-x-1">
                            <StatusIcon className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate font-medium">
                              {leave.employee.lastName} {leave.employee.firstName}
                            </span>
                          </div>
                          <div className="text-[10px] opacity-75 ml-4 truncate">
                            {typeConfig.label}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  <div className="border-t my-2" />
                </div>
              );
            })()}
            <div className="space-y-1">
              <button
                onClick={handleAddLeaveFromPopover}
                className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded hover:bg-muted transition-colors"
              >
                <CalendarIcon className="w-4 h-4" />
                <span>Dodaj nieobecność</span>
              </button>
              {isWorkDay(selectedDate) ? (
                <>
                  <button
                    onClick={handleEditWorkDayFromPopover}
                    className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded hover:bg-muted transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    <span>Edytuj dzien roboczy</span>
                  </button>
                  <button
                    onClick={handleRemoveWorkDay}
                    className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded hover:bg-muted transition-colors text-red-600"
                  >
                    <X className="w-4 h-4" />
                    <span>Usun dzien roboczy</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={handleAddWorkDayFromPopover}
                  className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded hover:bg-muted transition-colors"
                >
                  <Briefcase className="w-4 h-4" />
                  <span>Oznacz jako dzien roboczy</span>
                </button>
              )}
              {isAdmin && (
                <>
                  {isHoliday(selectedDate) ? (
                    <>
                      <button
                        onClick={handleEditHolidayFromPopover}
                        className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded hover:bg-muted transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                        <span>Edytuj święto</span>
                      </button>
                      <button
                        onClick={handleDeleteHolidayFromPopover}
                        className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded hover:bg-muted transition-colors text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Usuń święto</span>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleAddHolidayFromPopover}
                      className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded hover:bg-muted transition-colors"
                    >
                      <Palmtree className="w-4 h-4" />
                      <span>Dodaj święto</span>
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}

      {/* Work Day Dialog */}
      {showWorkDayDialog && selectedDate && (
        <Dialog open={showWorkDayDialog} onOpenChange={() => {
          setShowWorkDayDialog(false);
          setEditingWorkDayId(null);
        }}>
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>{editingWorkDayId ? 'Edytuj dzien roboczy' : 'Oznacz dzien roboczy'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-sm text-muted-foreground">
                  Data: <strong>{selectedDate.toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                </div>

                <div>
                  <Label htmlFor="workday-name">Nazwa/Powod *</Label>
                  <Input
                    id="workday-name"
                    value={workDayFormData.name}
                    onChange={(e) => setWorkDayFormData({ ...workDayFormData, name: e.target.value })}
                    placeholder="np. Odrobienie za 10.11"
                  />
                </div>

                <div>
                  <Label htmlFor="workday-description">Komentarz</Label>
                  <Input
                    id="workday-description"
                    value={workDayFormData.description}
                    onChange={(e) => setWorkDayFormData({ ...workDayFormData, description: e.target.value })}
                    placeholder="Dodatkowe informacje"
                  />
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm">
                  <p className="text-amber-800">
                    Godziny przepracowane w tym dniu beda liczone jako zwykle godziny pracy (nie jako nadgodziny 100%).
                  </p>
                </div>

                <div className="flex items-center justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => {
                    setShowWorkDayDialog(false);
                    setEditingWorkDayId(null);
                  }}>
                    <X className="w-4 h-4 mr-2" />
                    Anuluj
                  </Button>
                  <Button onClick={handleSaveWorkDay} disabled={!workDayFormData.name}>
                    <Check className="w-4 h-4 mr-2" />
                    {editingWorkDayId ? 'Zapisz zmiany' : 'Zapisz'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </Dialog>
      )}

      {/* Holiday Dialog */}
      {showHolidayDialog && (
        <Dialog open={showHolidayDialog} onOpenChange={() => {
          setShowHolidayDialog(false);
          setEditingHolidayId(null);
        }}>
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>{editingHolidayId ? 'Edytuj święto' : 'Dodaj święto'}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="holiday-name">Nazwa święta *</Label>
                  <Input
                    id="holiday-name"
                    value={holidayFormData.name}
                    onChange={(e) => setHolidayFormData({ ...holidayFormData, name: e.target.value })}
                    placeholder="np. Boże Narodzenie"
                  />
                </div>

                <div>
                  <Label htmlFor="holiday-date">Data *</Label>
                  <Input
                    id="holiday-date"
                    type="date"
                    value={holidayFormData.date}
                    onChange={(e) => setHolidayFormData({ ...holidayFormData, date: e.target.value })}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="holiday-isRecurring"
                    checked={holidayFormData.isRecurring}
                    onChange={(e) => setHolidayFormData({ ...holidayFormData, isRecurring: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="holiday-isRecurring" className="cursor-pointer">
                    Święto powtarzające się co roku
                  </Label>
                </div>

                <div>
                  <Label htmlFor="holiday-description">Opis</Label>
                  <Input
                    id="holiday-description"
                    value={holidayFormData.description}
                    onChange={(e) => setHolidayFormData({ ...holidayFormData, description: e.target.value })}
                    placeholder="Dodatkowe informacje"
                  />
                </div>

                <div className="flex items-center justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => {
                    setShowHolidayDialog(false);
                    setEditingHolidayId(null);
                  }}>
                    <X className="w-4 h-4 mr-2" />
                    Anuluj
                  </Button>
                  <Button onClick={handleSaveHoliday} disabled={!holidayFormData.name || !holidayFormData.date}>
                    <Check className="w-4 h-4 mr-2" />
                    {editingHolidayId ? 'Zapisz zmiany' : 'Dodaj'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </Dialog>
      )}

      {/* Holiday Delete Confirmation */}
      {deleteConfirmHoliday && (
        <Dialog open={!!deleteConfirmHoliday} onOpenChange={() => setDeleteConfirmHoliday(null)}>
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>Potwierdź usunięcie</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p>
                  Czy na pewno chcesz usunąć święto <strong>{deleteConfirmHoliday.name}</strong>?
                </p>
                <p className="text-sm text-muted-foreground">
                  Tej operacji nie można cofnąć.
                </p>
                <div className="flex items-center justify-end space-x-2 pt-4">
                  <Button variant="outline" onClick={() => setDeleteConfirmHoliday(null)}>
                    <X className="w-4 h-4 mr-2" />
                    Anuluj
                  </Button>
                  <Button variant="destructive" onClick={handleConfirmDeleteHoliday}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Usuń
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </Dialog>
      )}

      {/* Dialog Components */}
      <LeaveDialog
        open={showAddDialog}
        onClose={() => {
          setShowAddDialog(false);
          setPrefilledStartDate('');
        }}
        onSave={handleAddLeave}
        employees={employees}
        prefilledStartDate={prefilledStartDate}
      />

      <LeaveDetailsDialog
        leave={selectedLeave}
        open={showDetailsDialog}
        onClose={() => {
          setShowDetailsDialog(false);
          setSelectedLeave(null);
        }}
        onApprove={handleApproveLeave}
        onReject={handleRejectLeave}
        onDelete={handleDeleteLeave}
        loading={updateLoading || deleteLoading}
      />
    </div>
  );
};